import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Icon,
  Text,
} from '@kognitos/lattice';
import type { TContentSearchError } from '../content-search';

interface IContentSearchErrorAlertProps {
  error: TContentSearchError;
  /** Whether the list below still shows the results from before this search. */
  showsPreviousResults: boolean;
  onRetry: () => void;
  isRetrying?: boolean;
}

/**
 * Inline notice for a runs search the server refused because it reads file
 * contents: rate limited (with the wait it asked for) or past its time limit
 * (with how to narrow it). Shown above the list instead of an error page, so
 * the search box and the previous results stay put.
 *
 * @param props - See {@link IContentSearchErrorAlertProps}
 * @returns The alert
 */
export function ContentSearchErrorAlert({
  error,
  showsPreviousResults,
  onRetry,
  isRetrying = false,
}: IContentSearchErrorAlertProps) {
  const { t } = useTranslation('automation');
  const { t: tCommon } = useTranslation('common');

  const isRateLimited = error.kind === 'rateLimited';
  const title = isRateLimited
    ? t('automation.runs.contentSearch.error.rateLimited.title')
    : t('automation.runs.contentSearch.error.timedOut.title');
  const hint = isRateLimited
    ? error.retryAfterSeconds
      ? t('automation.runs.contentSearch.error.rateLimited.wait', {
          count: error.retryAfterSeconds,
        })
      : t('automation.runs.contentSearch.error.rateLimited.noWait')
    : t('automation.runs.contentSearch.error.timedOut.description');

  return (
    <Alert data-testid="runs-content-search-error">
      <Icon type={isRateLimited ? 'Clock' : 'AlertCircle'} />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        <Text level="small" color="muted">
          {showsPreviousResults
            ? `${hint} ${t('automation.runs.contentSearch.error.previousResults')}`
            : hint}
        </Text>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-1"
          onClick={onRetry}
          isLoading={isRetrying}
          data-testid="runs-content-search-retry"
        >
          {tCommon('common.action.retry')}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
